import { sleep } from 'src/utils/dateTime';

const MAX_CONCURRENT_RPC_REQUESTS = 4;
const MIN_REQUEST_SPACING_MS = 100;

/**
 * After a 429 the provider keeps rejecting for a while, so back the whole app
 * off rather than letting every caller keep retrying into the penalty box.
 */
const RATE_LIMIT_COOLDOWN_MS = 5_000;
const RATE_LIMITED_REQUEST_SPACING_MS = 400;

let activeRequests = 0;
let nextAvailableDispatchTime = 0;
let lastRateLimitedAt = 0;
const pendingQueue: Array<() => void> = [];

/**
 * Whether the RPC provider rate-limited us recently.
 *
 * Some providers omit `Access-Control-Allow-Origin` on a 429, so the browser
 * blocks the response and the rejection surfaces to JS as an untyped
 * `TypeError: Failed to fetch` with no status to inspect. Callers that need to
 * tell rate limiting apart from a genuine network failure consult this instead
 * of pattern-matching the error.
 *
 * Only covers 429s we could actually read: a CORS-blocked one never reaches the
 * status check below, so back-to-back blocked 429s stay indistinguishable from
 * a network failure. Closing that gap needs a same-origin RPC relay.
 */
export const wasRecentlyRateLimited = () =>
	Date.now() - lastRateLimitedAt < RATE_LIMIT_COOLDOWN_MS;

const getRequestSpacingMs = () =>
	wasRecentlyRateLimited()
		? RATE_LIMITED_REQUEST_SPACING_MS
		: MIN_REQUEST_SPACING_MS;

const acquireSlot = async (): Promise<void> => {
	if (activeRequests >= MAX_CONCURRENT_RPC_REQUESTS) {
		// At capacity: wait to be handed a slot directly by releaseSlot. The
		// slot is transferred to us (activeRequests already accounts for it), so
		// we must NOT re-increment on wake — otherwise a synchronous arrival
		// could read the count between decrement and re-increment and exceed the
		// cap.
		await new Promise<void>((resolve) => pendingQueue.push(resolve));
	} else {
		activeRequests++;
	}

	// Space dispatches out so a burst never exceeds
	// 1000 / MIN_REQUEST_SPACING_MS requests per second, widening the spacing
	// while we're in a rate-limit cooldown.
	//
	// The slot is claimed on the way out rather than up front: a reservation made
	// before a 429 lands would still fire on the old, tighter spacing, letting a
	// handful of requests through after the cooldown started.
	for (;;) {
		const now = Date.now();
		const wait = nextAvailableDispatchTime - now;

		if (wait <= 0) {
			nextAvailableDispatchTime = now + getRequestSpacingMs();
			return;
		}

		await sleep(wait);
	}
};

const releaseSlot = () => {
	const next = pendingQueue.shift();
	if (next) {
		// Hand our slot straight to the next waiter — the active count stays
		// unchanged (transfer, not release-then-reacquire).
		next();
	} else {
		activeRequests--;
	}
};

/**
 * Shared fetch for all Solana RPC `Connection`s. Caps concurrent in-flight
 * requests and spaces dispatches app-wide so bursts (page-load init, wallet
 * connect, resubscribes, connection retries) queue briefly instead of
 * tripping the RPC provider's burst rate limit — which fails ~all of the
 * burst and puts the client in a multi-second penalty box. The queue is
 * shared across every Connection created via generateConnection so
 * overlapping connections (e.g. during RPC failover) can't stack bursts.
 */
export const rateLimitedRpcFetch: typeof fetch = async (input, init) => {
	await acquireSlot();
	try {
		const response = await fetch(input, init);

		if (response.status === 429) {
			lastRateLimitedAt = Date.now();
		}

		return response;
	} finally {
		releaseSlot();
	}
};
